import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { User, Shield, Calendar, Pill, FileText, Pencil, Save, X, ChevronRight, Info, Camera, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import AppointmentCard from "@/components/AppointmentCard";
import Avatar from "@/components/Avatar";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import BiometricToggleCard from "@/components/BiometricToggleCard";
import ForumSettingsCard from "@/components/ForumSettingsCard";
import IntakeQLinkCard from "@/components/IntakeQLinkCard";
import ContactInfoCard from "@/components/ContactInfoCard";
import DeleteAccountCard from "@/components/DeleteAccountCard";
import { useToast } from "@/hooks/use-toast";

// Client-side resize to keep data URL small (max 512x512, JPEG 0.85)
function resizeImage(file, maxSize = 512, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
        else if (height >= width && height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(new File([blob], "avatar.jpg", { type: "image/jpeg" })) : reject(new Error("Canvas export failed")),
          "image/jpeg",
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, updateProfile, logout, checkAuth } = useAuth();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.full_name || "");
  const [savingName, setSavingName] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [refills, setRefills] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!user) return;
    API.get("/appointments").then(({ data }) => setAppointments(data || [])).catch(() => {});
    API.get("/refill-requests").then(({ data }) => setRefills(data || [])).catch(() => {});
  }, [user]);

  const saveName = async () => {
    setSavingName(true);
    await updateProfile({ full_name: nameInput });
    setSavingName(false); setEditingName(false);
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Unsupported file", description: "Please choose an image file.", variant: "destructive" });
      return;
    }
    setUploadingAvatar(true);
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append("file", resized);
      await API.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
      toast({ title: "Profile picture updated" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || err.message || "Try again", variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const removeAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await API.delete("/users/me/avatar");
      await checkAuth();
      toast({ title: "Profile picture removed" });
    } catch (err) {
      toast({ title: "Remove failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const isAdmin = user?.role === "admin";
  const upcomingAppointments = appointments.filter(a => a.status === "scheduled");
  const pastAppointments = appointments.filter(a => a.status !== "scheduled");
  const displayedRefills = isAdmin ? refills : refills.filter(r => ["pending", "under_review"].includes(r.status));

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 animate-fade-in" data-testid="profile-page">
      <PageHeader title="My Profile" subtitle="Manage your account and settings" />
      <div className="bg-primary rounded-2xl p-5 text-primary-foreground flex items-center gap-4" data-testid="profile-header">
        <div className="relative shrink-0">
          <Avatar src={user?.avatar_url} name={user?.full_name} email={user?.email} size="xl" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAvatar}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center shadow-lg hover:scale-105 transition-transform disabled:opacity-60"
            data-testid="profile-avatar-upload-button"
            aria-label="Change profile picture"
          >
            <Camera className="w-3.5 h-3.5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarFile}
            className="hidden"
            data-testid="profile-avatar-file-input"
          />
        </div>
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input className="bg-white/20 text-primary-foreground border border-white/30 rounded-lg px-2 py-1 text-sm font-semibold w-full outline-none" value={nameInput} onChange={e => setNameInput(e.target.value)} autoFocus />
              <button onClick={saveName} disabled={savingName} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30"><Save className="w-4 h-4" /></button>
              <button onClick={() => setEditingName(false)} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-display font-semibold truncate">{user?.full_name || user?.email?.split("@")[0]}</h1>
              <button onClick={() => { setNameInput(user?.full_name || ""); setEditingName(true); }} className="p-1 rounded-lg bg-white/20 hover:bg-white/30"><Pencil className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <p className="text-sm opacity-75">{user?.email}</p>
          {isAdmin && <div className="mt-1 flex items-center gap-1.5 text-xs opacity-75"><Shield className="w-3 h-3" /><span>Provider / Admin</span></div>}
          {user?.avatar_url && (
            <button
              onClick={removeAvatar}
              disabled={uploadingAvatar}
              className="mt-2 inline-flex items-center gap-1 text-[11px] opacity-75 hover:opacity-100"
              data-testid="profile-avatar-remove-button"
            >
              <Trash2 className="w-3 h-3" /> Remove photo
            </button>
          )}
        </div>
      </div>
      <div className={`grid gap-3 ${isAdmin ? "grid-cols-2" : "grid-cols-3"}`}>
        <SummaryCard icon={Calendar} label="Upcoming" value={upcomingAppointments.length} color="text-blue-600 bg-blue-50" />
        <SummaryCard icon={Pill} label={isAdmin ? "All Refills" : "Active Refills"} value={displayedRefills.length} color="text-amber-600 bg-amber-50" />
        {!isAdmin && <SummaryCard icon={FileText} label="Documents" value={0} color="text-rose-600 bg-rose-50" />}
      </div>
      <Tabs defaultValue="appointments">
        <TabsList className="w-full"><TabsTrigger value="appointments" className="flex-1">Appointments</TabsTrigger><TabsTrigger value="activity" className="flex-1">{isAdmin ? "Patient Refills" : "Activity"}</TabsTrigger></TabsList>
        <TabsContent value="appointments" className="mt-4 space-y-3">
          {upcomingAppointments.length === 0 && pastAppointments.length === 0 ? (
            <div className="text-center py-10"><Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No appointments on record.</p><Link to="/appointments" className="text-sm text-primary font-medium mt-2 inline-block">Book one now</Link></div>
          ) : (
            <>
              {upcomingAppointments.length > 0 && <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Upcoming</p><div className="space-y-2">{upcomingAppointments.map(a => <AppointmentCard key={a.id} appointment={a} compact />)}</div></div>}
              {pastAppointments.length > 0 && <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Recent History</p><div className="space-y-2">{pastAppointments.map(a => <AppointmentCard key={a.id} appointment={a} compact />)}</div></div>}
            </>
          )}
        </TabsContent>
        <TabsContent value="activity" className="mt-4 space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Pill className="w-4 h-4 text-secondary" />{isAdmin ? "All Patient Refills" : "Active Refill Requests"}</h3>
            {displayedRefills.length === 0 ? <p className="text-sm text-muted-foreground">No refill requests.</p> : (
              <div className="space-y-2">{displayedRefills.map(r => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="min-w-0 flex-1 pr-3"><p className="text-sm font-medium truncate">{r.medication} {r.strength}</p><p className="text-xs text-muted-foreground truncate">{isAdmin ? r.patient_name || r.patient_email : r.pharmacy_name}</p></div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold capitalize shrink-0">{r.status?.replace("_", " ")}</span>
                </div>
              ))}</div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <IntakeQLinkCard />
      <ContactInfoCard />
      <ForumSettingsCard />
      <ChangePasswordCard />
      <BiometricToggleCard />
      <DeleteAccountCard />
      <Button onClick={logout} variant="outline" className="w-full rounded-full" data-testid="profile-logout-button">Sign Out</Button>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3 text-center">
      <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center mx-auto mb-1.5`}><Icon className="w-4 h-4" /></div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
